import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { View } from "@src/components";
import { DataStateView } from "@src/components/screen-state/DataStateView";
import { useSelectedSiteStore } from "@src/stores/selected-site-store";
import { useVehicles, filterSignature, useCameras, getDefaultFilters } from "@visioni/a3s-vehicle-tracker/app";
import { groupsToTimeline, bucketByGroupId } from "@visioni/a3s-vehicle-tracker";
import type { SimilarFields, TimelineEntry, FlatVehicleDetection, VehicleFilters } from "@visioni/a3s-vehicle-tracker";
import { FilterPanel } from "../components/FilterPanel";
import { TimelineView } from "../components/TimelineView";
import { router } from "expo-router";
import { useTenantId } from "@src/features/tenants";
import { useVehicleFilters } from "../hooks/use-vehicle-filters";
import { useTimezoneSelector } from "../hooks/use-timezone-preference";

interface VehicleSearchScreenProps {
  /**
   * Explicit site scope from the URL. `undefined` means the route didn't
   * specify — fall back to the sidebar store (native behaviour). Pass `null`
   * to force all-sites mode (tenant-level web route).
   */
  siteIdFromRoute?: string | null;
}

export function VehicleSearchScreen({ siteIdFromRoute }: VehicleSearchScreenProps) {
  const storeSiteId = useSelectedSiteStore((s) => s.selectedSiteId);
  const setSelectedSiteId = useSelectedSiteStore((s) => s.setSelectedSiteId);

  // Sync route param → store on mount and external navigation (e.g. browser
  // back/forward). The store is the source of truth for in-page interactions
  // because it updates synchronously, whereas route params lag behind
  // router.push.
  useEffect(() => {
    if (siteIdFromRoute === undefined) return;
    if (siteIdFromRoute !== useSelectedSiteStore.getState().selectedSiteId) {
      setSelectedSiteId(siteIdFromRoute);
    }
  }, [siteIdFromRoute, setSelectedSiteId]);

  const selectedSiteId = storeSiteId;
  const tenantId = useTenantId();
  const { filters, committedFilters, updateFilter, setFilters, resetFilters } = useVehicleFilters();
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Pagination state is coupled to the exact query that produced it. `identity`
  // records which query the `cursor`/`pages` belong to; when the live query
  // identity differs we treat the state as empty for THIS render (see
  // `isStale` below) rather than deferring the reset to a useEffect, which
  // would leave one render where a cursor from the previous query is sent to
  // VT — VT trusts any well-formed cursor (ADR-0068 BL3) and would return the
  // wrong slice.
  const [pageState, setPageState] = useState<{
    identity: string;
    cursor: string | undefined;
    pages: FlatVehicleDetection[];
  }>({ identity: "", cursor: undefined, pages: [] });

  const { data: camerasData } = useCameras({ siteId: selectedSiteId });
  const {
    options: timezoneOptions,
    needsSelector: needsTimezoneSelector,
    activeTimezone,
    setActiveTimezone,
  } = useTimezoneSelector(camerasData?.cameras);

  const handleSortChange = useCallback((order: "asc" | "desc") => {
    setSortOrder(order);
  }, []);

  // Reset filters when tenant changes. Pagination reset is handled by the
  // query-identity check below (which includes tenantId), so no cursor reset here.
  const prevTenantRef = useRef(tenantId);
  useEffect(() => {
    if (prevTenantRef.current !== tenantId) {
      prevTenantRef.current = tenantId;
      resetFilters();
    }
  }, [tenantId, resetFilters]);

  // Clear camera selection when site changes. Pagination reset is handled below.
  const prevSiteRef = useRef(selectedSiteId);
  useEffect(() => {
    if (prevSiteRef.current !== selectedSiteId) {
      prevSiteRef.current = selectedSiteId;
      updateFilter("selectedCameras", {});
    }
  }, [selectedSiteId, updateFilter]);

  // Everything that changes which slice of results is valid. When this differs
  // from the identity our pagination state was captured for, the stored cursor
  // and accumulated pages belong to a previous query and must not be used.
  const queryIdentity = `${tenantId}|${selectedSiteId ?? ""}|${sortOrder}|${filterSignature(committedFilters)}`;
  const isStale = pageState.identity !== queryIdentity;
  // Derive from the LOCAL `isStale` boolean, never from a just-written ref —
  // so the request never carries a stale cursor and the timeline never shows
  // the previous query's rows on the render the identity changes.
  const effectiveCursor = isStale ? undefined : pageState.cursor;
  if (isStale) {
    setPageState({ identity: queryIdentity, cursor: undefined, pages: [] });
  }

  const { data, isLoading, isFetching, isPlaceholderData, error, refetch } = useVehicles({
    filters: committedFilters,
    selectedSiteId,
    cursor: effectiveCursor,
    sort: sortOrder,
  });

  const vehicles = useMemo(() => {
    const priorPages = isStale ? [] : pageState.pages;
    const pageVehicles: FlatVehicleDetection[] = data && "vehicles" in data ? data.vehicles : [];
    if (!effectiveCursor && pageVehicles.length > 0) {
      return pageVehicles;
    }
    if (pageVehicles.length > 0 && !isPlaceholderData) {
      return [...priorPages, ...pageVehicles];
    }
    return priorPages;
  }, [data, effectiveCursor, isStale, pageState.pages, isPlaceholderData]);

  const groups = useMemo(() => bucketByGroupId(vehicles), [vehicles]);

  const timelineEntries = useMemo((): TimelineEntry[] => {
    return groupsToTimeline(groups);
  }, [groups]);

  // A page can come back with zero groups but a non-null nextCursor (e.g. an
  // authorization-filtered or empty VT window). In that case we must keep
  // rendering the timeline with "load more" rather than showing the empty
  // state, so the user is not stranded before reachable results.
  const hasMore = !!data?.nextCursor;

  // Pass `undefined` (not an empty array) when the request errored with nothing
  // to show, so DataStateView surfaces the error UI with retry rather than the
  // "no results" empty state. A successful empty result still passes `[]`.
  const displayData = error && !data && groups.length === 0 ? undefined : timelineEntries;

  const handleClearAll = useCallback(() => {
    resetFilters();
    setSelectedSiteId(null);
    if (siteIdFromRoute !== undefined) {
      router.push(`/tenants/${tenantId}/vehicles` as never);
    }
  }, [resetFilters, setSelectedSiteId, siteIdFromRoute, tenantId]);

  const handleSiteChange = useCallback(
    (siteId: string | null) => {
      setSelectedSiteId(siteId);
      if (siteIdFromRoute !== undefined) {
        const path = siteId ? `/tenants/${tenantId}/vehicles?siteIds=${siteId}` : `/tenants/${tenantId}/vehicles`;
        router.push(path as never);
      }
    },
    [setSelectedSiteId, siteIdFromRoute, tenantId],
  );

  const handleLoadMore = useCallback(() => {
    if (data?.nextCursor) {
      setPageState({ identity: queryIdentity, cursor: data.nextCursor, pages: vehicles });
    }
  }, [data, vehicles, queryIdentity]);

  const handleFindSimilar = useCallback(
    (fields: SimilarFields) => {
      // Start from defaults so prior match criteria (plate, description,
      // colour, attributes, load status from an earlier search) don't silently
      // narrow the "find similar" results. Preserve only scope, not match
      // criteria: the date window and any selected cameras/site. Read these
      // from the live `filters` draft (not `committedFilters`) so edits the
      // user just made but that haven't cleared the 400ms debounce yet are
      // still honoured.
      const next: VehicleFilters = {
        ...getDefaultFilters(),
        startDate: filters.startDate,
        endDate: filters.endDate,
        selectedCameras: filters.selectedCameras,
      };
      if (fields.plateText) next.plateText = fields.plateText;
      if (fields.make) next.make = fields.make;
      if (fields.model) next.model = fields.model;
      if (fields.color) next.color = fields.color;
      if (fields.types?.length) next.types = fields.types;
      if (fields.loadStatus) next.loadStatuses = [fields.loadStatus];
      if (fields.attributes) next.attributes = fields.attributes;

      setFilters(next);
    },
    [filters, setFilters],
  );

  return (
    <View flex={1} bg="background" pt="6xl">
      <FilterPanel
        filters={filters}
        onFiltersChange={(key, value) => updateFilter(key, value)}
        onClearAll={handleClearAll}
        selectedSiteId={selectedSiteId}
        onSiteChange={handleSiteChange}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
        activeTimezone={activeTimezone}
        timezoneOptions={timezoneOptions}
        needsTimezoneSelector={needsTimezoneSelector}
        onTimezoneChange={setActiveTimezone}
      />

      <DataStateView
        data={displayData}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error as Error | null}
        refetch={() => void refetch()}
        isEmpty={(items) => items.length === 0 && !hasMore}
        loadingMessage="Searching vehicles..."
        errorTitle="Unable to search vehicles"
        emptyState={{
          icon: "truck",
          title: "No vehicles found",
          message: "Try adjusting your search filters or date range.",
        }}
      >
        {(entries) => (
          <TimelineView
            entries={entries}
            loading={isFetching}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            activeTimezone={activeTimezone}
            actions={{
              onFindSimilar: handleFindSimilar,
            }}
          />
        )}
      </DataStateView>
    </View>
  );
}
